import { IconState } from '@/components';
import { ChipColor } from '@/components/chips/types';
import { IconName } from '@/components/icons/types';
import { FileSchema } from '@/features/files';
import { isObject } from '@/types';

export type TechnologyType = 'solar' | 'wind' | 'bess' | 'hydrogen' | 'biogas' | 'hidropower' | 'datacenter' | 'topco';

export type TechnologyCollection =
  | 'projects_bess_technologies'
  | 'projects_solar_technologies'
  | 'projects_wind_technologies'
  | 'projects_hydrogen_technologies'
  | 'projects_biogas_technologies'
  | 'projects_hidropower_technologies'
  | 'projects_datacenter_technologies'
  | 'projects_topco_technologies';

export type ProjectStatus = 'draft' | 'pending' | 'published' | 'archived' | 'closed';

export type LandType = 'rental' | 'purchase' | 'other';

export type DUPStatus = 'pending' | 'requested' | 'obtained' | 'not-required';

export type ConnectionType = 'underground' | 'aerial';

export type ConnectionTo = 'powerline' | 'substation';

export type PermitType = 'connection' | 'environmental' | 'previous' | 'construction' | 'other';

export type PermitStatus = 'pending' | 'requested' | 'obtained' | 'not-required';

export type UpgradingTechnology = 'membrane' | 'water_scrubbing' | 'psa';

export type Permit = {
  id?: string;
  type: PermitType;
  title: string | null;
  status: PermitStatus;
  date: string | null;
  project?: string;
};

export type ConnectionInfrastructure = {
  id?: number;
  project: string;
  evacuation_line_total_distance?: number | null;
  evacuation_line_voltage: number;
  connection_type: string[];
  connection_to: string[];
  shared_infrastructure?: boolean | null;
  shared_infrastructure_comments?: string | null;
  line_estimated_costs: number;
  technical_and_economical_conditions_costs: number | null;
  connection_costs_comments: string;
  connection_infrastructure_comments: string;
  gas_grid_connection_pressure: number | null;
  distance_to_gas_injection_point: number | null;
};

export type LandRights = {
  id?: number;
  project?: string;
  land_surface: number;
  land_types: string[];
  land_price: number;
  land_contract_duration: string;
  land_contracted_percentage: number;
  land_comments: string;
  dup_status: string;
  dup_date: string;
  dup_comments: string;
};

export type CapexAndOpex = {
  id?: number;
  project?: string;
  estimated_epc: number;
  epc_comments: string;
  tsa: number;
  tsa_details: string;
  bop: number;
  bop_details: string;
  om_price: number;
  om_term: number;
  om_comments: string;
  other_comments: string;
};

export type OtherInfo = {
  id?: number;
  project?: string;
  energy_offset: string;
  energy_offset_details: string;
  financial_details: string;
  other_comments: string;
   type_of_revenue: string;
};

export type PortfolioSummary = {
  id?: number;
  project?: string;
  technologies: string[];
  markets: string[];
  total_mw: number;
  details: string;
};

export type FinancialHighlights = {
  id?: number;
  project?: string;
  details: string;
};

export type TeamAndGovernance = {
  id?: number;
  project?: string;
  details: string;
};

export type SolarProjectTechnology = {
  id: string;
  development_status?: string;
  installed_capacity_peak: number | null;
  installed_capacity_nominal: number | null;
  specific_production: number | null;
  ready_to_build: string | null;
  commercial_operation_date: string | null;
  structure_type: string | null;
  module_type: string | null;
  module_power: number | null;
  panel_configuration: string | null;
  pitch_distance: number | null;
  comments: string | null;
  basic_design_comments: string | null;
};

export type WindProjectTechnology = {
  id: string;
  development_status?: string;
  installed_capacity: number | null;
  specific_production: number | null;
  ready_to_build: string | null;
  commercial_operation_date: string | null;
  turbine_model: string | null;
  turbine_power: number | null;
  turbiness: number | null;
  comments: string | null;
  basic_design_comments: string | null;
};

export type BESSProjectTechnology = {
  id: string;
  development_status?: string;
  installed_capacity: number | null;
  injection_capacity: number | null;
  consumption_capacity: number | null;
  ready_to_build: string | null;
  commercial_operation_date: string | null;
  storage_technology: string | null;
  storage_capacity: number | null;
  hours_cycles: number | null;
  comments: string | null;
  basic_design_comments?: string | null;
};

export type BiogasProjectTechnology = {
  id: string;
  capacity: number | null;
  development_status?: string;
  ready_to_build: string | null;
  commercial_operation_date: string | null;
  feedstock_supply_radius: number | null;
  feedstock_cost: number | null;
  upgrading_included: boolean | null;
  upgrading_technology: string | null;
  information: string | null;
};

export type HydrogenProjectTechnology = {
  id: string;
  installed_capacity: string | null;
  development_status?: string;
  information: string | null;
};

export type HidropowerProjectTechnology = {
  id: string;
  installed_capacity: number | null;
  type_project: string;
  storage_hours: number | null;
  specific_production: number | null;
  development_status?: string;
  ready_to_build: string | null;
  commercial_operation_date: string | null;
  turbine_capacity: number | null;
  pump_capacity: number | null;
  generation_storage_capacity: number | null;
  comments: string | null;
};

export type DatacenterProjectTechnology = {
  id: string;
  installed_capacity: number | null;
  type_project: string;
  development_status?: string;
  ready_to_build: string | null;
  //commercial_operation_date: string | null;
  it_capacity: number | null;
  pue: string | null;
  data_center_type: string | null;
  redundancy: string | null;
  cooling_technology: string | null;
  comments: string | null;
};

export type TopcoProjectTechnology = {
  id: number;
  company_type: string | null;
  other_details: string | null;
  hq_location: string | null;
  development_status?: never;
};

export type ProjectTechnology = {
  id: number;
  project: Project;
  collection: TechnologyCollection;
  files: { file: FileSchema }[];
  item:
    | SolarProjectTechnology
    | WindProjectTechnology
    | BESSProjectTechnology
    | BiogasProjectTechnology
    | HydrogenProjectTechnology
    | HidropowerProjectTechnology
    | DatacenterProjectTechnology
    | TopcoProjectTechnology;
  land_surface: number | null;
  land_types: LandType[] | null;
  land_price: number | null;
  land_contract_duration: string | null;
  land_contracted_percentage: number | null;
  land_comments: string | null;
  land_gross_built_area: number | null;
  dup_status: DUPStatus | null;
  dup_date: string | null;
  dup_comments: string | null;
  evacuation_line_total_distance: number | null;
  evacuation_line_voltage: number | null;
  connection_type: ConnectionType[] | null;
  connection_to: ConnectionTo[] | null;
  shared_infrastructure: boolean | null;
  shared_infrastructure_comments: string | null;
  connection_costs: number | null;
  connection_costs_comments: string | null;
  connection_infrastructure_comments: string | null;
  estimated_epc: number | null;
  epc_comments: string | null;
  om_price: number | null;
  om_term: number | null;
  om_comments: string | null;
  insurance_price: number | null;
  insurance_comments: string | null;
  has_ppa: boolean | null;
  ppa_comments: string | null;
  has_debt: boolean | null;
  debt_comments: string | null;
  permits: Permit[] | null;
  permiting_comments: string | null;
  portfolio_technologies: string[] | null;
  portfolio_total_mw: number | null;
  portfolio_markets: string[] | null;
  portfolio_details: string | null;
  financial_details: string | null;
  team_governance_details: string | null;
};

export type Project = {
  id: string;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
  status: ProjectStatus;
  company: string;
  name?: string;
  country: string;
  city: string;
  other_details?: string;
  permits_comments?: string;
  technologies: ProjectTechnology[];
  permits?: Permit[];
  connection_infrastructure: ConnectionInfrastructure[];
  land_rights: LandRights[];
  capex_and_opex: CapexAndOpex[];
  other_info: OtherInfo[];
  portfolio_summary: PortfolioSummary[];
  financial_highlights: FinancialHighlights[];
  team_and_governance: TeamAndGovernance[];
};

export function isSolarProjectTechnology(project: unknown): project is SolarProjectTechnology {
  const keys = [
    'id',
    'development_status',
    'installed_capacity_peak',
    'installed_capacity_nominal',
    'specific_production',
    'ready_to_build',
    'commercial_operation_date',
    'structure_type',
    'module_type',
    'module_power',
    'panel_configuration',
    'pitch_distance',
    'comments',
    'basic_design_comments',
  ];
  return isObject(project) && keys.every((key) => key in project);
}

export function isBiogasProjectTechnology(project: unknown): project is BiogasProjectTechnology {
  const keys = [
    'id',
    'capacity',
    'development_status',
    'ready_to_build',
    'commercial_operation_date',
    'feedstock_supply_radius',
    'feedstock_cost',
    'upgrading_included',
    'upgrading_technology',
    'information',
  ];
  return isObject(project) && keys.every((key) => key in project);
}

export type Location = {
  country: string;
  city: string[];
};

export type ProjectItemList = {
  id: string;
  title: string;
  location: Location;
  technologies: {
    types: TechnologyType[];
    icons: {
      name: IconName[];
      states: IconState[];
    };
    state: {
      list: string[];
    };
    capacity: number | null;
  };
  status: {
    name: string;
    color: ChipColor;
  };
  isDraft: boolean;
  isPending: boolean;
  isPublished: boolean;
  isArchived: boolean;
};

export type ProjectHeader = {
  id: string;
  title: string;
  company: string;
  location: Location;
  technologies: {
    icons: IconState[];
  };
  status: {
    name: string;
    color: ChipColor;
  };
};

export type Technology = {
  id: number;
  project: string;
  collection: TechnologyCollection;
  type: TechnologyType;
  details: TechnologyDetails | TopcoTechnologyDetails;
};

export type ProjectDetails = {
  id: string;
  company: string;
  location: Location;
  technologies: Technology[];
  isDraft: boolean;
  isPublished: boolean;
  otherDetails?: string;
  permits?: Permit[];
  permitsComments?: string;
  connectionInfrastructure: ConnectionInfrastructure[];
  landRights: LandRights[];
  capexAndOpex: CapexAndOpex[];
  otherInfo: OtherInfo[];
  name?: string;
  portfolioSummary?: PortfolioSummary[];
  financialHighlights?: FinancialHighlights[];
  teamGovernance?: TeamAndGovernance[];
};

export type SolarItem = {
  id: string;
  installedCapacityPeak: number | null;
  installedCapacityNominal: number | null;
  specificProduction: number | null;
  readyToBuild: string | null;
  commercialOperationDate: string | null;
  structureType: string | null;
  moduleType: string | null;
  modulePower: number | null;
  panelConfiguration: string | null;
  pitchDistance: number | null;
  generalComments: string | null;
  basicDesignComments: string | null;
};

export type WindItem = {
  id: string;
  installedCapacity: number | null;
  specificProduction: number | null;
  readyToBuild: string | null;
  commercialOperationDate: string | null;
  turbineModel: string | null;
  turbinePower: number | null;
  turbiness: number | null;
  generalComments: string | null;
  basicDesignComments: string | null;
};

export type BessItem = {
  id: string;
  installedCapacity: number | null;
  injectionCapacity: number | null;
  readyToBuild: string | null;
  commercialOperationDate: string | null;
  storageTechnology: string | null;
  storageCapacity: number | null;
  hoursCycles: number | null;
  generalComments: string | null;
  basicDesignComments: string | null;
};

export type BiogasItem = {
  id: string;
  capacity: number | null;
  development_status?: string;
  ready_to_build: string | null;
  commercial_operation_date: string | null;
  feedstock_supply_radius: number | null;
  feedstock_cost: number | null;
  upgrading_included: boolean | null;
  upgrading_technology: string | null;
  information: string | null;
};

export type HydrogenItem = {
  id: string;
  information: string | null;
};

export type HidropowerItem = {
  id: string;
  installedCapacity: number | null;
  typeProject: string;
  storageHours: number | null;
  specificProduction: number | null;
  developmentStatus?: string;
  readyToBuild: string | null;
  commercialOperationDate: string | null;
  turbineCapacity: number | null;
  pumpCapacity: number | null;
  generationStorageCapacity: number | null;
  generalComments: string | null;
};

export type DatacenterItem = {
  id: string;
  installedCapacity: number | null;
  typeProject: string;
  developmentStatus?: string;
  readyToBuild: string | null;
  //commercialOperationDate: string | null;
  itCapacity: number | null;
  pue: string | null;
  dataCenterType: string | null;
  redundancy: string | null;
  coolingTechnology: string | null;
  generalComments: string | null;
};

export type TopcoItem = {
  id: number;
  hqLocation: string | null;
  companyType: string | null;
  otherDetails: string | null;
};

export type TechnologyDetails = {
  id: number;
  developmentStatus: {
    status?: string;
  };
  item: SolarItem | WindItem | BessItem | BiogasItem | HydrogenItem | HidropowerItem | DatacenterItem | TopcoItem;
  files: FileSchema[];
  permittingPLA: {
    permits: Permit[] | null;
    comments: string | null;
  };
  landRights: {
    surface: number | null;
    grossBuiltArea: number | null;
    type: LandType[] | null;
    price: number | null;
    contractDuration: string | null;
    contractedPercentage: number | null;
    comments: string | null;
    dupStatus: DUPStatus | null;
    dupDate: string | null;
    dupComments: string | null;
  };
  connectionInfrastructure: {
    evacuationLineTotalDistance: number | null;
    evacuationLineVoltage: number | null;
    connectionType: ConnectionType[] | null;
    connectionTo: ConnectionTo[] | null;
    connectionCosts: number | null;
    connectionCostsComments: string | null;
    sharedInfrastructure: boolean | null;
    sharedInfrastructureComments: string | null;
    connectionInfrastructureComments: string | null;
  };
  capexOpex: {
    estimatedEpc: number | null;
    epcComments: string | null;
    omPrice: number | null;
    omTerm: number | null;
    omComments: string | null;
    insurancePrice: number | null;
    insuranceComments: string | null;
  };
  others: {
    hasPpa: boolean | null;
    ppaComments: string | null;
    hasDebt: boolean | null;
    debtComments: string | null;
  };
};

export type TopcoTechnologyDetails = {
  id: number;
  files: FileSchema[];
  item: TopcoItem;
};
